import React from 'react';
import ReactDOM from 'react-dom';
import { ThemeProvider as ThemeProvider$1 } from 'styled-components';
import { theme, useId, useSyncedState } from '@primer/react';
import deepmerge from 'deepmerge';
import { jsx, jsxs } from 'react/jsx-runtime';

const defaultColorMode = 'day';
const defaultDayScheme = 'light';
const defaultNightScheme = 'dark';

// eslint-disable-next-line @typescript-eslint/no-explicit-any

const ThemeContext = /*#__PURE__*/React.createContext({
  setColorMode: () => null,
  setDayScheme: () => null,
  setNightScheme: () => null
});

// inspired from __NEXT_DATA__, we use application/json to avoid CSRF policy with inline scripts
const getServerHandoff = id => {
  try {
    const serverData = document.getElementById(`__PRIMER_DATA_${id}__`)?.textContent;
    if (serverData) return JSON.parse(serverData);
  } catch (_error) {
    // if document/element does not exist or JSON is invalid, supress error
  }
  return {};
};
const ThemeProvider = ({
  children,
  ...props
}) => {
  // Get fallback values from parent ThemeProvider (if exists)
  const {
    theme: fallbackTheme,
    colorMode: fallbackColorMode,
    dayScheme: fallbackDayScheme,
    nightScheme: fallbackNightScheme
  } = useTheme();

  // Initialize state
  const theme$1 = props.theme ?? fallbackTheme ?? theme;
  const uniqueDataId = useId();
  const {
    resolvedServerColorMode
  } = getServerHandoff(uniqueDataId);
  const resolvedColorModePassthrough = React.useRef(resolvedServerColorMode);
  const [colorMode, setColorMode] = useSyncedState(props.colorMode ?? fallbackColorMode ?? defaultColorMode);
  const [dayScheme, setDayScheme] = useSyncedState(props.dayScheme ?? fallbackDayScheme ?? defaultDayScheme);
  const [nightScheme, setNightScheme] = useSyncedState(props.nightScheme ?? fallbackNightScheme ?? defaultNightScheme);
  const systemColorMode = useSystemColorMode();
  // eslint-disable-next-line react-hooks/refs
  const resolvedColorMode = resolvedColorModePassthrough.current || resolveColorMode(colorMode, systemColorMode);
  const colorScheme = chooseColorScheme(resolvedColorMode, dayScheme, nightScheme);
  const {
    resolvedTheme,
    resolvedColorScheme
  } = React.useMemo(() => applyColorScheme(theme$1, colorScheme), [theme$1, colorScheme]);

  // this effect will only run on client
  React.useEffect(function updateColorModeAfterServerPassthrough() {
    const resolvedColorModeOnClient = resolveColorMode(colorMode, systemColorMode);
    if (resolvedColorModePassthrough.current) {
      // if the resolved color mode passed on from the server is not the resolved color mode on client, change it!
      if (resolvedColorModePassthrough.current !== resolvedColorModeOnClient) {
        window.setTimeout(() => {
          // use ReactDOM.flushSync to prevent automatic batching of state updates since React 18
          // ref: https://github.com/reactwg/react-18/discussions/21
          ReactDOM.flushSync(() => {
            // override colorMode to whatever is resolved on the client to get a re-render
            setColorMode(resolvedColorModeOnClient);
          });

          // immediately after that, set the colorMode to what the user passed to respond to system color mode changes
          setColorMode(colorMode);
        });
      }
      resolvedColorModePassthrough.current = null;
    }
  }, [colorMode, systemColorMode, setColorMode]);
  return /*#__PURE__*/jsx(ThemeContext.Provider, {
    value: {
      theme: resolvedTheme,
      colorScheme,
      colorMode,
      resolvedColorMode,
      resolvedColorScheme,
      dayScheme,
      nightScheme,
      setColorMode,
      setDayScheme,
      setNightScheme
    },
    children: /*#__PURE__*/jsxs(ThemeProvider$1, {
      theme: resolvedTheme,
      children: [children, props.preventSSRMismatch ? /*#__PURE__*/jsx("script", {
        type: "application/json",
        id: `__PRIMER_DATA_${uniqueDataId}__`,
        dangerouslySetInnerHTML: {
          __html: JSON.stringify({
            resolvedServerColorMode: resolvedColorMode
          })
        }
      }) : null]
    })
  });
};
function useTheme() {
  return React.useContext(ThemeContext);
}
function useColorSchemeVar(values, fallback) {
  const {
    colorScheme = ''
  } = useTheme();
  return values[colorScheme] ?? fallback;
}
function useSystemColorMode() {
  const [systemColorMode, setSystemColorMode] = React.useState(getSystemColorMode);
  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const media = window?.matchMedia?.('(prefers-color-scheme: dark)');
    function matchesMediaToColorMode(matches) {
      return matches ? 'night' : 'day';
    }
    function handleChange(event) {
      const isNight = event.matches;
      setSystemColorMode(matchesMediaToColorMode(isNight));
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (media) {
      // just in case the preference changed before the event listener was attached
      const isNight = media.matches;
      setSystemColorMode(matchesMediaToColorMode(isNight));
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (media.addEventListener !== undefined) {
        media.addEventListener('change', handleChange);
        return function cleanup() {
          media.removeEventListener('change', handleChange);
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      else if (media.addListener !== undefined) {
        media.addListener(handleChange);
        return function cleanup() {
          media.removeListener(handleChange);
        };
      }
    }
  }, []);
  return systemColorMode;
}
function getSystemColorMode() {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
    return 'night';
  }
  return 'day';
}
function resolveColorMode(colorMode, systemColorMode) {
  switch (colorMode) {
    case 'auto':
      return systemColorMode;
    default:
      return colorMode;
  }
}
function chooseColorScheme(colorMode, dayScheme, nightScheme) {
  switch (colorMode) {
    case 'day':
    case 'light':
      return dayScheme;
    case 'dark':
    case 'night':
      return nightScheme;
  }
}
function applyColorScheme(theme, colorScheme) {
  if (!theme.colorSchemes) {
    return {
      resolvedTheme: theme,
      resolvedColorScheme: undefined
    };
  }
  if (!theme.colorSchemes[colorScheme]) {
    // eslint-disable-next-line no-console
    console.error(`\`${colorScheme}\` scheme not defined in \`theme.colorSchemes\``);

    // Apply the first defined color scheme
    const defaultColorScheme = Object.keys(theme.colorSchemes)[0];
    return {
      resolvedTheme: deepmerge(theme, theme.colorSchemes[defaultColorScheme]),
      resolvedColorScheme: defaultColorScheme
    };
  }
  return {
    resolvedTheme: deepmerge(theme, theme.colorSchemes[colorScheme]),
    resolvedColorScheme: colorScheme
  };
}

export { ThemeProvider, ThemeProvider as default, defaultColorMode, useColorSchemeVar, useTheme };
